// MT TAN Master API service — Metadata + CRUD

import {
  MT_INDIA_TAN_CSI_UPLOAD_API,
  MT_INDIA_TAN_MASTER_GET_API,
  MT_INDIA_TAN_MASTER_SAVE_API,
  MT_INDIA_TAN_MASTER_UPDATE_API,
  MT_MASTER_META_DATA,
  MT_TAN_MASTER_DELETE_API,
  MT_TAN_MASTER_DOWNLOAD_API,
  MT_TAN_MASTER_GET_API,
  MT_TAN_MASTER_LIST_API,
  MT_TAN_MASTER_SAVE_API,
  MT_TAN_MASTER_UPDATE_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  MTApiResponse,
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtParseJsonResponse,
  mtParseMessageResponse,
  mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'
import { getAccessToken } from '@/utils/session'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MTTanMasterSaveRequest {
  countryCode: string
  entityMasterUuid: string
  attributes: Record<string, string | number | boolean>
}

export interface MTTanMasterUpdateRequest {
  uuid: string
  countryCode: string
  entityMasterUuid: string
  attributes: Record<string, string | number | boolean>
}

export interface MTTanMasterListSearchCO {
  fieldName: string
  value: string
  operator: string
  filterOperator: string
}

export interface MTTanMasterListRequest {
  pageFilter: {
    currentPage: number
    pageSize: number
    sortField?: string
    sortOrder?: 'ASC' | 'DESC'
    includeCount?: boolean
  }
  searchCOList: MTTanMasterListSearchCO[] | any[]
}

// ─── Metadata ────────────────────────────────────────────────────────────────

/**
 * Map raw API metadata field to the MTTinField shape expected by the UI.
 * The API returns `field_key`, `type`, `order`, `grid_column` etc.,
 * whereas the UI interface uses `key`, `dataType`, `displayOrder`.
 */
function mapMetadataField(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const typeMap: Record<string, string> = {
    text: 'STRING',
    number: 'DECIMAL',
    date: 'TIMESTAMP',
    json: 'JSON',
  }

  // Map tan_group to section: 'responsible_person_details' → 'responsible_person', 'entity_details' → undefined
  const tanGroup = (raw as any).tan_group as string | undefined
  const section = tanGroup === 'responsible_person_details'
    ? 'responsible_person'
    : tanGroup === 'entity_details'
      ? undefined
      : (raw as any).section

  return {
    key: raw.field_key ?? raw.key,
    label: raw.label,
    dataType:
      typeMap[(raw.type as string)?.toLowerCase?.()] ??
      raw.dataType ??
      'STRING',
    required: raw.required ?? false,
    searchable: (raw as any).searchable ?? false,
    sortable: (raw as any).sortable ?? false,
    visible: raw.visible ?? true,
    editable: raw.editable ?? true,
    displayOrder: raw.order ?? (raw as any).displayOrder ?? 0,
    section,
    tan_group: tanGroup,
    grid_column: (raw as any).grid_column,
    system: (raw as any).system,
    validations: raw.validations ?? {},
    type: raw.type,
    options: raw.options,
    placeholder: raw.placeholder,
  }
}

/**
 * Fetch master metadata for a given country code and data type.
 * GET /wht-mt-admin/api/v1/data/{countryCode}/{dataType}/metadata
 */
export const fetchMasterMetadata = async <T = unknown>(
  countryCode: string,
  dataType: string
): Promise<MTApiResponse<T>> => {
  try {
    const endpoint = MT_MASTER_META_DATA(countryCode, dataType)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    const result = await mtParseJsonResponse<any>(response)

    // Map API field shape (field_key, type, order) to UI shape (key, dataType, displayOrder)
    if (
      result.success &&
      result.data?.fields &&
      Array.isArray(result.data.fields)
    ) {
      result.data.fields = result.data.fields.map(mapMetadataField)
    }

    return result as MTApiResponse<T>
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Convenience: Fetch TAN Master metadata.
 * GET /wht-mt-admin/api/v1/data/{countryCode}/TAN_MASTER/metadata
 */
export const fetchTanMasterMetadata = async <T = unknown>(
  countryCode: string
): Promise<MTApiResponse<T>> => {
  return fetchMasterMetadata<T>(countryCode, 'TAN_MASTER')
}

// ─── Create ──────────────────────────────────────────────────────────────────

/**
 * Create a new TAN Master record.
 * POST /wht-mt-admin/api/v1/tan-master/save
 */
export const saveTanMaster = async (
  payload: MTTanMasterSaveRequest
): Promise<MTApiResponse<string>> => {
  try {
    const url = mtUrlBuilder(MT_TAN_MASTER_SAVE_API)
    const options = mtBuildRequestHeader('POST', payload)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── Update ──────────────────────────────────────────────────────────────────

/**
 * Update an existing TAN Master record.
 * PUT /wht-mt-admin/api/v1/tan-master/update
 */
export const updateTanMaster = async (
  payload: MTTanMasterUpdateRequest
): Promise<MTApiResponse<string>> => {
  try {
    const url = mtUrlBuilder(MT_TAN_MASTER_UPDATE_API)
    const options = mtBuildRequestHeader('PUT', payload)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── List ────────────────────────────────────────────────────────────────────

/**
 * List TAN Master records globally with pagination and search.
 * POST /wht-mt-admin/api/v1/tan-master/global/list
 */
export const listTanMaster = async <T = unknown>(
  payload: MTTanMasterListRequest
): Promise<MTApiResponse<T>> => {
  try {
    const url = mtUrlBuilder(MT_TAN_MASTER_LIST_API)
    const options = mtBuildRequestHeader('POST', payload)
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── Delete ──────────────────────────────────────────────────────────────────

/**
 * Delete a TAN Master record.
 * DELETE /wht-mt-admin/api/v1/tan-master/{countryCode}/{uuid}
 */
export const deleteTanMaster = async (
  countryCode: string,
  uuid: string
): Promise<MTApiResponse<string>> => {
  try {
    const endpoint = MT_TAN_MASTER_DELETE_API(countryCode, uuid)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('DELETE')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── Get by ID ───────────────────────────────────────────────────────────────

/**
 * Fetch a single TAN Master record by country code and UUID.
 * GET /wht-mt-admin/api/v1/tan-master/{countryCode}/{uuid}
 */
export const getTanMasterById = async <T = unknown>(
  countryCode: string,
  uuid: string
): Promise<MTApiResponse<T>> => {
  try {
    const endpoint = MT_TAN_MASTER_GET_API(countryCode, uuid)
    const url = mtUrlBuilder(endpoint)
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) {
      return await mtHandleRequestFailure(response)
    }

    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── Download ────────────────────────────────────────────────────────────────

/**
 * Download TAN Master records as Excel.
 * POST /wht-mt-admin/api/v1/tan-master/global/download
 */
export const downloadTanMaster = async (
  payload: MTTanMasterListRequest
): Promise<MTApiResponse<Blob>> => {
  const url = mtUrlBuilder(MT_TAN_MASTER_DOWNLOAD_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// ─── India TAN (embedded India TIN form in MT flow) ────────────────────────────
// These mirror the legacy India adminTAN behavior but hit the MT endpoints.
// The India form builds a flat `tanMasterData` payload (via buildTanMasterPayload),
// uploads a CSI file first, then saves with the returned file name.

/**
 * Upload a CSI file for India TAN validation.
 * POST wht-mt-admin/api/v1/tan-master/csi/file/upload  (multipart/form-data)
 * Returns the uploaded file name/reference used by the save call.
 */
export const uploadIndiaCsiFile = async (
  file: File
): Promise<MTApiResponse<string>> => {
  try {
    const url = mtUrlBuilder(MT_INDIA_TAN_CSI_UPLOAD_API)
    const token = getAccessToken()
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        // NOTE: do NOT set Content-Type; the browser sets the multipart boundary.
        Authorization: token ?? '',
        Accept: '*/*',
      },
      body: formData,
    })

    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Create an India TAN Master record.
 * POST wht-mt-admin/api/v1/tan-master/india/save/{fileUuid}
 * Mirrors the legacy India saveTanMaster: the uploaded CSI file reference
 * (fileUuid) is a URL path segment, and tanMasterData is the request body.
 */
export const saveIndiaTanMaster = async (
  tanMasterData: Record<string, unknown>,
  fileUuid: string
): Promise<MTApiResponse<string>> => {
  try {
    const url = mtUrlBuilder(MT_INDIA_TAN_MASTER_SAVE_API, { fileUuid })
    const options = mtBuildRequestHeader('POST', tanMasterData, {
      Accept: '*/*',
    })
    const response = await fetch(url, options)

    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch India TAN detail for the edit form by tan uuid.
 * GET wht-mt-admin/api/v1/tan-master/india/{uuid}
 * The tan uuid is passed as a URL path param. The response mirrors the India
 * list shape ({ content: [...] }) or may be a single record.
 */
export const getIndiaTanByUuid = async <T = unknown>(
  uuid: string
): Promise<MTApiResponse<T>> => {
  try {
    const url = mtUrlBuilder(MT_INDIA_TAN_MASTER_GET_API, { uuid })
    const options = mtBuildRequestHeader('GET')
    const response = await fetch(url, options)

    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<T>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Update an existing India TAN Master record.
 * PUT wht-mt-admin/api/v1/tan-master/india/update/{uuid}
 * Sends the same payload shape as create (built via buildTanMasterPayload).
 */
export const updateIndiaTanMaster = async (
  tanMasterData: Record<string, unknown>,
  uuid: string
): Promise<MTApiResponse<string>> => {
  try {
    const url = mtUrlBuilder(MT_INDIA_TAN_MASTER_UPDATE_API, { uuid })
    const options = mtBuildRequestHeader('PUT', tanMasterData, {
      Accept: '*/*',
    })
    const response = await fetch(url, options)

    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
